export type BlockType = 'image' | 'text' | 'link' | 'pdf';
export type ChannelVisibility = 'public' | 'private' | 'password' | 'hidden';

export interface ProfileRow {
  id: string;
  handle?: string;
  name: string;
  bio: string | null;
  website: string | null;
  website_url: string | null;
  joined: string | null;
  created_at: string | null;
  updated_at: string | null;
  avatar_path: string | null;
}

export interface Profile {
  id: string;
  handle?: string;
  name: string;
  bio: string | null;
  website: string | null;
  websiteUrl: string | null;
  joined: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  avatarPath: string | null;
}

export interface ChannelRow {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  url: string | null;
  accent_color: string | null;
  border_color: string | null;
  position: number | null;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
  visibility?: ChannelVisibility;
  has_password: boolean;
  handle?: string;
  blocks?: Array<{ count: number }>;
}

export interface Channel {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  url: string | null;
  accentColor: string | null;
  borderColor: string | null;
  position: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string;
  visibility: ChannelVisibility;
  hasPassword: boolean;
  handle?: string;
  blocksCount: number;
}

export interface BlockRow {
  id: string;
  channel_id: string;
  type: BlockType;
  title: string;
  content: string | null;
  description: string | null;
  image_path: string | null;
  pdf_path: string | null;
  source_url: string | null;
  source_author: string | null;
  position: number | null;
  created_at: string | null;
  updated_at: string | null;
  user_id: string;
  /** Present on search rows (blocks joined to channels). */
  channel_slug?: string | null;
  channels?: {
    title: string;
    slug: string;
    updated_at: string | null;
    visibility?: ChannelVisibility;
    has_password: boolean;
  } | null;
}

export interface Block {
  id: string;
  channelId: string;
  type: BlockType;
  title: string;
  content: string | null;
  description: string | null;
  imagePath: string | null;
  pdfPath: string | null;
  sourceUrl: string | null;
  sourceAuthor: string | null;
  position: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string;
  /** Present on search results (blocks joined to channels). */
  channelSlug?: string;
  channel?: {
    title: string;
    slug: string;
    updatedAt: string | null;
    visibility?: ChannelVisibility;
    hasPassword: boolean;
  };
}

export interface BlockPreviewRow {
  id: string;
  type: BlockType;
  title: string;
  content: string | null;
  image_path: string | null;
  created_at: string | null;
  channel_id: string;
}

export interface BlockPreview {
  id: string;
  type: BlockType;
  title: string;
  content: string | null;
  imagePath: string | null;
  createdAt: string | null;
  channelId: string;
}

export interface CommentRow {
  id: string;
  block_id: string;
  content: string;
  created_at: string | null;
  updated_at: string | null;
  user_id: string | null;
  author_name: string | null;
}

export interface Comment {
  id: string;
  blockId: string;
  content: string;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string | null;
  authorName: string | null;
}

export interface CreateChannelInput {
  title: string;
  slug: string;
  visibility?: ChannelVisibility;
  description?: string | null;
  url?: string | null;
  accentColor?: string | null;
  borderColor?: string | null;
  password?: string | null;
  userId: string;
}

export interface CreateBlockInput {
  channelId: string;
  type: BlockType;
  title: string;
  content?: string | null;
  sourceUrl?: string | null;
  sourceAuthor?: string | null;
  previewImagePath?: string | null;
  userId: string;
}

export type UpdateProfileInput = Partial<{
  name: string | null;
  bio: string | null;
  website: string | null;
  websiteUrl: string | null;
  avatarPath: string | null;
  website_url: string | null;
  avatar_path: string | null;
}>;

export type UpdateChannelInput = Partial<{
  title: string | null;
  visibility: ChannelVisibility;
  description: string | null;
  url: string | null;
  accentColor: string | null;
  borderColor: string | null;
  accent_color: string | null;
  border_color: string | null;
}>;

export type UpdateBlockInput = Partial<{
  title: string | null;
  content: string | null;
  description: string | null;
  sourceUrl: string | null;
  sourceAuthor: string | null;
  imagePath: string | null;
  pdfPath: string | null;
  source_url: string | null;
  source_author: string | null;
  image_path: string | null;
  pdf_path: string | null;
}>;

export function toProfile(row: ProfileRow): Profile {
  return {
    id: row.id,
    handle: row.handle,
    name: row.name,
    bio: row.bio,
    website: row.website,
    websiteUrl: row.website_url,
    joined: row.joined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    avatarPath: row.avatar_path,
  };
}

export function toChannel(row: ChannelRow): Channel {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    description: row.description,
    url: row.url,
    accentColor: row.accent_color,
    borderColor: row.border_color,
    position: row.position,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    userId: row.user_id,
    visibility: row.visibility || (row.has_password ? 'password' : 'public'),
    hasPassword: !!row.has_password,
    handle: row.handle,
    blocksCount: row.blocks?.[0]?.count ?? 0,
  };
}

export function toBlock(row: BlockRow): Block {
  return {
    id: row.id,
    channelId: row.channel_id,
    type: row.type,
    title: row.title,
    content: row.content,
    description: row.description,
    imagePath: row.image_path,
    pdfPath: row.pdf_path,
    sourceUrl: row.source_url,
    sourceAuthor: row.source_author,
    position: row.position,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    userId: row.user_id,
    channelSlug: row.channel_slug ?? undefined,
    channel: row.channels
      ? {
          title: row.channels.title,
          slug: row.channels.slug,
          updatedAt: row.channels.updated_at,
          visibility: row.channels.visibility,
          hasPassword: row.channels.has_password,
        }
      : undefined,
  };
}

export function toBlockPreview(row: BlockPreviewRow): BlockPreview {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    content: row.content,
    imagePath: row.image_path,
    createdAt: row.created_at,
    channelId: row.channel_id,
  };
}

export function toComment(row: CommentRow): Comment {
  return {
    id: row.id,
    blockId: row.block_id,
    content: row.content,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    userId: row.user_id,
    authorName: row.author_name,
  };
}

export function toProfileUpdateRow(updates: UpdateProfileInput): Record<string, unknown> {
  return {
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.bio !== undefined ? { bio: updates.bio } : {}),
    ...(updates.website !== undefined ? { website: updates.website } : {}),
    ...(updates.websiteUrl !== undefined ? { website_url: updates.websiteUrl } : {}),
    ...(updates.avatarPath !== undefined ? { avatar_path: updates.avatarPath } : {}),
    ...(updates.website_url !== undefined ? { website_url: updates.website_url } : {}),
    ...(updates.avatar_path !== undefined ? { avatar_path: updates.avatar_path } : {}),
  };
}

export function toChannelUpdateRow(updates: UpdateChannelInput): Record<string, unknown> {
  return {
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.visibility !== undefined ? { visibility: updates.visibility } : {}),
    ...(updates.description !== undefined ? { description: updates.description } : {}),
    ...(updates.url !== undefined ? { url: updates.url } : {}),
    ...(updates.accentColor !== undefined ? { accent_color: updates.accentColor } : {}),
    ...(updates.borderColor !== undefined ? { border_color: updates.borderColor } : {}),
    ...(updates.accent_color !== undefined ? { accent_color: updates.accent_color } : {}),
    ...(updates.border_color !== undefined ? { border_color: updates.border_color } : {}),
  };
}

export function toBlockUpdateRow(updates: UpdateBlockInput): Record<string, unknown> {
  return {
    ...(updates.title !== undefined ? { title: updates.title } : {}),
    ...(updates.content !== undefined ? { content: updates.content } : {}),
    ...(updates.description !== undefined ? { description: updates.description } : {}),
    ...(updates.sourceUrl !== undefined ? { source_url: updates.sourceUrl } : {}),
    ...(updates.sourceAuthor !== undefined ? { source_author: updates.sourceAuthor } : {}),
    ...(updates.imagePath !== undefined ? { image_path: updates.imagePath } : {}),
    ...(updates.pdfPath !== undefined ? { pdf_path: updates.pdfPath } : {}),
    ...(updates.source_url !== undefined ? { source_url: updates.source_url } : {}),
    ...(updates.source_author !== undefined ? { source_author: updates.source_author } : {}),
    ...(updates.image_path !== undefined ? { image_path: updates.image_path } : {}),
    ...(updates.pdf_path !== undefined ? { pdf_path: updates.pdf_path } : {}),
  };
}
